import { clientWithApiClient } from "../../client";
import type { ApiClient } from "../../types";
import type {
  ReviewsListResponse,
  ReviewStatsResponse,
  ReviewResponse,
  ReviewsListParams,
  UpdateReviewParams,
  PublicReviewRequestResponse,
  SubmitReviewParams,
  SubmitReviewResponse,
  ForceSendReviewEmailResponse,
  ReplyToReviewPayload,
} from "./types";

export const buildReviewsListQueryString = (params?: ReviewsListParams) => {
  const searchParams = new URLSearchParams();
  if (!params) return "";

  if (params.page) searchParams.set("page", params.page.toString());
  if (params.page_size)
    searchParams.set("page_size", params.page_size.toString());
  if (params.rating) searchParams.set("rating", params.rating.toString());
  if (params.min_rating)
    searchParams.set("min_rating", params.min_rating.toString());
  if (params.vehicle_id) searchParams.set("vehicle_id", params.vehicle_id);
  if (params.date_from) searchParams.set("date_from", params.date_from);
  if (params.date_to) searchParams.set("date_to", params.date_to);
  if (typeof params.responded === "boolean")
    searchParams.set("responded", params.responded ? "true" : "false");

  return searchParams.toString();
};

/**
 * List reviews with pagination and filters
 */
const list = (apiClient: ApiClient, params?: ReviewsListParams) => {
  const queryString = buildReviewsListQueryString(params);
  const url = queryString ? `/reviews?${queryString}` : "/reviews";

  return clientWithApiClient<ReviewsListResponse>(apiClient, url);
};

/**
 * Get review statistics
 */
const getStats = (apiClient: ApiClient, params?: { vehicle_id?: string }) => {
  const searchParams = new URLSearchParams();
  if (params?.vehicle_id) {
    searchParams.set("vehicle_id", params.vehicle_id);
  }
  const queryString = searchParams.toString();
  const url = queryString ? `/reviews/stats?${queryString}` : "/reviews/stats";
  return clientWithApiClient<ReviewStatsResponse>(apiClient, url);
};

/**
 * Get a single review by ID
 */
const get = (apiClient: ApiClient, id: string) =>
  clientWithApiClient<ReviewResponse>(apiClient, `/reviews/${id}`);

/**
 * Update a review (visibility)
 */
const update = (apiClient: ApiClient, id: string, params: UpdateReviewParams) =>
  clientWithApiClient<ReviewResponse>(apiClient, `/reviews/${id}`, {
    method: "PATCH",
    body: JSON.stringify(params),
    headers: {
      "Content-Type": "application/json",
    },
  });

/**
 * Get public review request by token
 */
const getPublicReviewRequest = (apiClient: ApiClient, token: string) =>
  clientWithApiClient<PublicReviewRequestResponse>(
    apiClient,
    `/public/reviews/${token}`,
  );

/**
 * Submit a review
 */
const submitReview = (apiClient: ApiClient, params: SubmitReviewParams) =>
  clientWithApiClient<SubmitReviewResponse>(apiClient, "/public/reviews", {
    method: "POST",
    body: JSON.stringify(params),
    headers: {
      "Content-Type": "application/json",
    },
  });

/**
 * Force send review email for a rental (debug/testing)
 *
 * Bypasses normal status and date checks. Used for testing/debugging purposes.
 * Creates a ReviewRequest if one doesn't exist, or reuses an existing one.
 *
 * @param apiClient - The API client
 * @param rentalId - The rental's public_id
 */
const forceSendReviewEmail = (apiClient: ApiClient, rentalId: string) =>
  clientWithApiClient<ForceSendReviewEmailResponse>(
    apiClient,
    `/rentals/${rentalId}/force-review-email`,
    {
      method: "POST",
    },
  );

/**
 * Reply to a review as the fleet manager (creates or overwrites existing reply).
 */
const reply = (
  apiClient: ApiClient,
  id: string,
  payload: ReplyToReviewPayload,
) =>
  clientWithApiClient<ReviewResponse>(apiClient, `/reviews/${id}/reply`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" },
  });

/**
 * Remove the manager reply from a review.
 */
const deleteReply = (apiClient: ApiClient, id: string) =>
  clientWithApiClient<ReviewResponse>(apiClient, `/reviews/${id}/reply`, {
    method: "DELETE",
  });

/**
 * Public endpoint: mark a review as helpful (increments helpful_count).
 */
const markHelpful = (apiClient: ApiClient, id: string) =>
  clientWithApiClient<{ data: { id: string; helpful_count: number } }>(
    apiClient,
    `/public/reviews/${id}/helpful`,
    { method: "POST" },
  );

export const reviews = {
  list,
  getStats,
  get,
  update,
  reply,
  deleteReply,
  markHelpful,
  getPublicReviewRequest,
  submitReview,
  forceSendReviewEmail,
};

export default reviews;
